import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {Anchor, Modal, Skeleton, createStyles} from '@mantine/core';
import {useDisclosure, useHotkeys} from '@mantine/hooks';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {OMNIBOX_ANALYTICS_ACTIONS} from 'constants/analytics-products/OmniboxAnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {openChat, openDraftChatInSidepanel} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {showCreateEntryModal} from 'nav/CreateDropdownActions';
import {
  getDefaultSearchSearchParams,
  isModelTypeEnabledForFiltering,
} from 'nav/sidepanel/listing/SearchSearchParams';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import AnimatedBorderOverlay from 'omnibox/AnimatedBorderOverlay';
import AnimatedReveal from 'omnibox/AnimatedReveal';
import OmniboxAction from 'omnibox/OmniboxAction';
import type {OmniboxActionItem} from 'omnibox/OmniboxAction';
import {
  fetchOmniboxAttachments,
  fetchRecentSearches,
  recordSearch,
  streamOmniboxQuery,
} from 'omnibox/OmniboxDAO';
import OmniboxFilterTabs from 'omnibox/OmniboxFilterTabs';
import type {OmniboxFilterTabId} from 'omnibox/OmniboxFilterTabs';
import OmniboxFooter from 'omnibox/OmniboxFooter';
import {subscribeToOmniboxOpen} from 'omnibox/OmniboxOpenControl';
import OmniboxQuickActions from 'omnibox/OmniboxQuickActions';
import {fetchOmniboxSavedSearches} from 'omnibox/OmniboxSavedSearchesDAO';
import type {RecentSearch} from 'omnibox/omniboxTypes';
import QuickSearchInput from 'omnibox/QuickSearchInput';
import useOmniboxKeyboardNav from 'omnibox/useOmniboxKeyboardNav';
import useOmniboxStream from 'omnibox/useOmniboxStream';
import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import useTypeaheadData from 'omnibox/useTypeaheadData';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  cleanUpOmniboxSearchProfilerEvents,
  OMNIBOX_SEARCH_RESULTS_RENDERED_EVENT,
  OMNIBOX_SEARCH_STARTED_EVENT,
} from 'performance/processSearchEvents';
import {setTaskSearchFilter} from 'processes/home/AssignedProcessTaskStatusSummary';
import {getTypesSupportingFilter} from 'search/FiltersByType';
import type {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {FilterKey, SortKey, keyToEmptyFilter} from 'search/SearchConstants';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import EntityStore from 'stores/EntityStore';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';

import {
  abortAttachmentPreview,
  buildActions,
  buildDefaultActions,
  buildSavedSearchActions,
  getAiChatContextId,
  startAiChatWithResultContext,
} from './omniboxActionBuilders';
import type {TypeToggleId} from './omniboxConstants';
import {
  ANSWER_GROUP,
  ADVANCED_SEARCH_GROUP,
  ASK_BENCHLING_GROUP,
  getTypeToggleFilters,
  getTypeToggleModelTypes,
  SAVED_SEARCHES_GROUP,
  SEARCH_RESULTS_GROUP,
} from './omniboxConstants';
import {extractFilterParams} from './omniboxFilterUtils';

const useStyles = createStyles((theme) => ({
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto' as const,
    minHeight: 0,
    // 8px bottom padding per the container spec.
    paddingBottom: 8,
    // Middle section of the omnibox frame: side borders only, per Figma.
    borderLeft: `1px solid ${Color.GRAY_400}`,
    borderRight: `1px solid ${Color.GRAY_400}`,
  },
  errorMessage: {
    padding: 16,
    textAlign: 'center' as const,
    color: '#e03131',
    fontSize: 14,
  },
  srOnly: {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap' as const,
    border: 0,
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.55px',
    color: '#656566',
    padding: '6px 16px',
  },
  recentSearchesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 16px',
  },
  recentSearchesTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.55px',
    color: '#656566',
  },
  advancedSearchLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    fontWeight: 700,
    color: theme.colors.blue[6],
    whiteSpace: 'nowrap' as const,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  // Raw px, not Mantine size props: rem() + the app's 10px root would shrink numeric props to
  // 62.5%. Mirrors the OmniboxAction row: minHeight 48, padding 2px/16px, icon 32, row gap 12.
  skeletonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 48,
    padding: '2px 16px',
  },
  skeletonIcon: {
    width: 32,
    height: 32,
    flexShrink: 0,
  },
  // Wider gap than the real row's 2px: the text bars are thinner than real text line boxes, so
  // extra gap keeps their centers where the real title/subtitle lines would sit.
  skeletonContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  skeletonTitle: {
    height: 10,
    width: '45%',
  },
  skeletonDescription: {
    height: 6,
    width: '30%',
  },
}));

const SKELETON_ROW_COUNT = 5;

// Mirrors a real OmniboxAction result row. Decorative: hidden from assistive tech (the live
// region already announces "Searching..."). Skeletons fill their px-sized parent at 100%, which
// passes through rem() unchanged, so the createStyles px sizing is what renders.
function OmniboxResultsSkeleton(): JSX.Element {
  const {classes} = useStyles();
  return (
    <div aria-hidden="true" {...getTestProps({component: 'Omnibox', element: 'results-skeleton'})}>
      {Array.from({length: SKELETON_ROW_COUNT}).map((_, index) => (
        <div key={index} className={classes.skeletonRow}>
          <div className={classes.skeletonIcon}>
            <Skeleton height="100%" width="100%" circle={true} />
          </div>
          <div className={classes.skeletonContent}>
            <div className={classes.skeletonTitle}>
              <Skeleton height="100%" width="100%" radius="sm" />
            </div>
            <div className={classes.skeletonDescription}>
              <Skeleton height="100%" width="100%" radius="sm" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Omnibox(): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedAnswer, setCopiedAnswer] = useState(false);
  const [recentSearches, setRecentSearches] = useState<Array<RecentSearch>>([]);
  const [savedSearches, setSavedSearches] = useState<Array<SavedSearch>>([]);
  const [showCompletionFlash, setShowCompletionFlash] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Array<OmniboxFilter>>([]);
  // The Entry / Entity type-toggle pills are mutually exclusive with each other and with a Type
  // dropdown selection, so at most one type filter is active. A toggle's expanded `type` filters are
  // merged into the search filters at query time (rather than living in selectedFilters) so they
  // don't render as removable chips.
  const [activeTypeToggle, setActiveTypeToggle] = useState<TypeToggleId | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastRequestId, setLastRequestId] = useState<string | null>(null);
  const [opened, {open, close, toggle}] = useDisclosure(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const queriesSubmittedRef = useRef(0);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // The Type dropdown's options, built from the schemas loaded up front. Author and folder options are
  // loaded by their own pills.
  const {typeTreeNodes} = useTypeaheadData();

  // Which filter pills render as active: the selected type toggle (if any) plus the author pill
  // when at least one author filter is applied.
  const activeTabIds = useMemo((): Set<OmniboxFilterTabId> => {
    const ids = new Set<OmniboxFilterTabId>();
    if (activeTypeToggle) {
      ids.add(activeTypeToggle);
    }
    if (selectedFilters.some((f) => f.kind === 'user')) {
      ids.add('user');
    }
    if (selectedFilters.some((f) => f.kind === 'type' || f.kind === 'schema')) {
      ids.add('type');
    }
    if (selectedFilters.some((f) => f.kind === 'project' || f.kind === 'folder')) {
      ids.add('project');
    }
    return ids;
  }, [activeTypeToggle, selectedFilters]);

  // Filters sent to search = user-selected chip filters + the active type toggle's expanded types.
  const searchFilters = useMemo((): Array<OmniboxFilter> => {
    if (!activeTypeToggle) {
      return selectedFilters;
    }
    return [...selectedFilters, ...getTypeToggleFilters(activeTypeToggle)];
  }, [selectedFilters, activeTypeToggle]);

  // Types the active type toggle or Type dropdown selection narrows search to, or null when no type is
  // selected.
  const selectedSearchTypes = useMemo((): Array<ModelTypeConstants> | null => {
    const selectedTypeFilter = selectedFilters.find((f) => f.kind === 'type' || f.kind === 'schema');
    const types: ReadonlyArray<ModelTypeConstants> | null =
      activeTypeToggle != null
        ? getTypeToggleModelTypes(activeTypeToggle)
        : selectedTypeFilter?.searchTypes != null
          ? (selectedTypeFilter.searchTypes as ReadonlyArray<ModelTypeConstants>)
          : null;
    return types?.filter((type) => isModelTypeEnabledForFiltering(type)) ?? null;
  }, [activeTypeToggle, selectedFilters]);

  // Author filtering only applies when at least one selected type has authors. Otherwise Authored By
  // is hidden and any selected author is dropped, since Advanced Search would reject it.
  const authorFilterSupported =
    selectedSearchTypes == null || getTypesSupportingFilter(FilterKey.AUTHOR, selectedSearchTypes).length > 0;
  useEffect(() => {
    if (!authorFilterSupported) {
      setSelectedFilters((prev) =>
        prev.some((f) => f.kind === 'user') ? prev.filter((f) => f.kind !== 'user') : prev
      );
    }
  }, [authorFilterSupported]);

  // Show the filter pills (Entry / Entity / Type / Project or Folder / Authored By) once the user has engaged with
  // search: typed a query, selected a filter, or activated a type toggle. Derived from existing
  // state so it can't drift.
  const showFilterPills = query.length > 0 || selectedFilters.length > 0 || activeTypeToggle != null;

  // Streaming omnibox results via Ably
  const {
    results,
    setResults,
    attachmentResults,
    setAttachmentResults,
    helpDocsResults,
    setHelpDocsResults,
    requestIdRef,
  } = useOmniboxStream();

  // Refs to avoid recreating handleKeyDownCapture on every results/loading change
  const resultsRef = useRef(results);
  resultsRef.current = results;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  // Tracks whether the user has submitted a query whose results are being shown.
  // Used to determine if filter changes should trigger an automatic re-query.
  const lastSubmittedQueryRef = useRef<string | null>(null);
  const usedArrowKeysRef = useRef(false);
  // Shared id on every event from one open/close cycle of the palette, so a session can be reconstructed
  // end to end. Null only while the palette is closed.
  const searchSessionIdRef = useRef<string | null>(null);

  // Register keyboard shortcut to open/close the palette
  // Pass empty tagsToIgnore + triggerOnContentEditable so it fires from inputs/textareas
  useHotkeys([['mod+j', toggle]], [], true);

  // Let other surfaces (e.g. the navbar search icon) open the omnibox while it's mounted. Gated by
  // a tenant config flag (default off) so the sidebar trigger — and the button/dialog semantics the
  // navbar derives from it — can be turned on without affecting the Cmd+J shortcut above. The
  // omnibox only mounts when it is enabled (see OmniboxContainer), so this trigger is implicitly
  // gated on that too.
  useEffect(() => {
    if (!BenchlingConfig.SEARCH_ENABLE_OMNIBOX_SIDEBAR_TRIGGER) {
      return undefined;
    }
    return subscribeToOmniboxOpen(open);
  }, [open]);

  // Auto-focus input when opened
  useEffect(() => {
    if (opened) {
      queriesSubmittedRef.current = 0;
      // Allow resubmitting the same query in a new session
      lastSubmittedQueryRef.current = null;
      searchSessionIdRef.current = crypto.randomUUID();
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.OPENED_OMNIBOX, {
        searchSessionId: searchSessionIdRef.current,
      });
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [opened]);

  useEffect(() => {
    if (!opened || query) {
      return;
    }
    fetchRecentSearches()
      .then((items) => {
        if (mountedRef.current) {
          setRecentSearches(items);
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch recent searches', e);
      });
  }, [opened, query]);

  // Fetch the user's saved searches once the palette opens so they can be name-matched against the
  // typed query. Unlike recent searches, these surface only when a query is present, so the fetch is
  // keyed on `opened` alone; it refetches on each open to stay current with create/delete/share flows.
  useEffect(() => {
    if (!opened) {
      return;
    }
    // Clear the previous open's list and ignore this fetch once the effect is torn down, so a reopen
    // never shows a since-deleted/revoked search before the refetch lands, and a slow response from an
    // earlier open can't overwrite a newer one.
    let cancelled = false;
    setSavedSearches([]);
    fetchOmniboxSavedSearches()
      .then((items) => {
        if (!cancelled && mountedRef.current) {
          setSavedSearches(items);
        }
      })
      .catch((e) => {
        logging.error('Failed to fetch saved searches', {extra: {error: e}});
      });
    return () => {
      cancelled = true;
    };
  }, [opened]);

  useEffect(() => {
    if (!copiedAnswer) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setCopiedAnswer(false);
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [copiedAnswer]);

  const handleCopyAnswer = useCallback(() => {
    setCopiedAnswer(true);
  }, []);

  // Flash a brief completion indicator when loading finishes with results
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !loading && results) {
      setShowCompletionFlash(true);
      const timer = setTimeout(() => {
        setShowCompletionFlash(false);
      }, 600);
      return () => {
        clearTimeout(timer);
      };
    }
    prevLoadingRef.current = loading;
    return undefined;
  }, [loading, results]);

  const executeSearchRef =
    useRef<(query: string, filters: Array<OmniboxFilter>, opts: {isRequery: boolean}) => void>(noop);
  // Set from handleAdvancedSearch below (defined after this memo). Lets the first "Search in Advanced
  // Search" result row trigger the same navigation without hoisting handleAdvancedSearch above it.
  const advancedSearchTriggerRef = useRef<() => void>(noop);
  // Gate AI chat launch and chat reopen on the Ask Benchling agent being enabled.
  const aiChatEnabled = isAgentTypeEnabled(
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? AgentType.ASSISTANT : AgentType.ASK
  );
  const defaultActions = useMemo(
    () =>
      buildDefaultActions(
        recentSearches,
        (query) => {
          setQuery(query);
          executeSearchRef.current(query, [], {isRequery: false});
        },
        // Dismissal is handled by the chat entry's closeOnTrigger flag.
        aiChatEnabled
          ? (chatApiId) => {
              openChat(chatApiId);
            }
          : undefined
      ),
    [recentSearches, aiChatEnabled]
  );

  // Name-matched saved searches, pinned above the streamed results once a query is typed.
  const savedSearchActions = useMemo(
    () => buildSavedSearchActions(savedSearches, query),
    [savedSearches, query]
  );

  const actions = useMemo((): Array<OmniboxActionItem> => {
    // Render only once both the main search and the parallel attachment fetch have resolved, so the
    // entity results and their attachment matches appear together instead of one popping in first.
    if (results && attachmentResults) {
      // The first row always escalates the current query to the full Advanced Search page. It leads
      // the list in its own headerless section, above the saved searches and streamed results.
      const advancedSearchAction: OmniboxActionItem = {
        id: 'advanced-search',
        title: `Search for "${query}" in Advanced search`,
        group: ADVANCED_SEARCH_GROUP,
        resultType: 'advanced_search',
        closeOnTrigger: false,
        onTrigger: () => {
          advancedSearchTriggerRef.current();
        },
      };
      // The advanced-search escalation leads, then the name-matched saved searches as their own group,
      // then the streamed search results.
      return [
        advancedSearchAction,
        ...savedSearchActions,
        ...buildActions(results, copiedAnswer, handleCopyAnswer, helpDocsResults, attachmentResults),
      ];
    }

    if (!query) {
      return defaultActions;
    }

    return [];
  }, [
    results,
    query,
    copiedAnswer,
    handleCopyAnswer,
    defaultActions,
    savedSearchActions,
    helpDocsResults,
    attachmentResults,
  ]);

  const hasNavigableResults = actions.length > 0;

  const handleClose = useCallback(
    (closeMethod: 'escape' | 'clicked_result' | 'clicked_outside' = 'clicked_outside') => {
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLOSED_OMNIBOX, {
        searchSessionId: searchSessionIdRef.current,
        closeMethod,
        queriesSubmitted: queriesSubmittedRef.current,
        hasResults: resultsRef.current != null,
        resultCount: resultsRef.current
          ? resultsRef.current.searchResults.length + resultsRef.current.contentResults.length
          : 0,
        filterCount: selectedFilters.length,
        usedArrowKeys: usedArrowKeysRef.current,
      });
      usedArrowKeysRef.current = false;
      close();
      requestIdRef.current = null;
      lastSubmittedQueryRef.current = null;
      searchSessionIdRef.current = null;
      abortAttachmentPreview();
      setResults(null);
      setAttachmentResults(null);
      setLoading(false);
      setQuery('');
      setSelectedFilters([]);
      setActiveTypeToggle(null);
      setShowCompletionFlash(false);
      setHelpDocsResults(new Map());
      setErrorMessage(null);
    },
    [close, setResults, setAttachmentResults, setHelpDocsResults, requestIdRef, selectedFilters.length]
  );

  const executeSearch = useCallback(
    (searchQuery: string, filters: Array<OmniboxFilter>, {isRequery}: {isRequery: boolean}) => {
      const queryWords = searchQuery.trim().split(/\s+/).length;
      if (!isRequery) {
        queriesSubmittedRef.current += 1;
        trackEvent(OMNIBOX_ANALYTICS_ACTIONS.SUBMITTED_QUERY, {
          searchSessionId: searchSessionIdRef.current,
          queryLength: searchQuery.length,
          queryWords,
          filterCount: filters.length,
        });
        recordSearch(searchQuery).catch(noop);
        setRecentSearches((prev) => {
          const without = prev.filter((s) => s.queryString !== searchQuery);
          return [{queryString: searchQuery}, ...without].slice(0, 10);
        });
      }

      const {activeId, workspaceItems} = WorkspaceStore.getState();
      setLoading(true);
      setResults(null);
      setAttachmentResults(null);
      setHelpDocsResults(new Map());

      const requestId = crypto.randomUUID();
      requestIdRef.current = requestId;
      setLastRequestId(requestId);
      lastSubmittedQueryRef.current = searchQuery;
      setErrorMessage(null);

      const {schemaApiIds, objectTypes, searchTypes, projectApiIds, folderApiIds, authorApiIds} =
        extractFilterParams(filters);

      const filterParams = {
        schemaApiIds: schemaApiIds.length > 0 ? schemaApiIds : undefined,
        objectTypes: objectTypes.length > 0 ? objectTypes : undefined,
        searchTypes: searchTypes.length > 0 ? searchTypes : undefined,
        projectApiIds: projectApiIds.length > 0 ? projectApiIds : undefined,
        folderApiIds: folderApiIds.length > 0 ? folderApiIds : undefined,
        authorApiIds: authorApiIds.length > 0 ? authorApiIds : undefined,
      };

      // Attachment matches always resolve to a parent Entry, so a type filter that excludes Entry
      // can only yield an empty section (matches the backend's Entry-scope gate). Skip the request
      // entirely in that case. An empty type filter (no toggle) leaves every type in scope.
      const entryInScope =
        (objectTypes.length === 0 || objectTypes.includes('Entry')) &&
        (searchTypes.length === 0 || searchTypes.includes(ModelTypeConstants.ENTRY));
      if (!entryInScope) {
        setAttachmentResults([]);
      }
      // Fetch attachment matches in parallel so retrieval overlaps the main entity search. Both
      // slices render in a single paint (see the actions memo), so we keep the search in the
      // loading state until both settle rather than letting the faster response render on its own.
      const attachmentsSettled = entryInScope
        ? fetchOmniboxAttachments({query: searchQuery, ...filterParams})
            .then(({attachmentResults: fetched}) => {
              if (mountedRef.current && requestIdRef.current === requestId) {
                setAttachmentResults(fetched);
              }
            })
            .catch((e) => {
              // Attachment matches are supplementary; log to telemetry and leave the section empty.
              logging.error('Failed to fetch omnibox attachments', {extra: {error: e}});
              if (mountedRef.current && requestIdRef.current === requestId) {
                setAttachmentResults([]);
              }
            })
        : Promise.resolve();

      cleanUpOmniboxSearchProfilerEvents();
      logProfilerTimingEvent({...OMNIBOX_SEARCH_STARTED_EVENT, id: requestId});
      const mainSettled = streamOmniboxQuery({
        query: searchQuery,
        requestId,
        activeWorkspaceTabId: activeId ?? undefined,
        openWorkspaceTabIds: workspaceItems.map((item) => item.fileId),
        ...filterParams,
      })
        .then((finalResults) => {
          if (mountedRef.current && requestIdRef.current === requestId) {
            setResults(finalResults);
            trackEvent(OMNIBOX_ANALYTICS_ACTIONS.DISPLAYED_RESULTS, {
              searchSessionId: searchSessionIdRef.current,
              queryLength: searchQuery.length,
              queryWords,
              searchResultCount: finalResults.searchResults.length,
              contentResultCount: finalResults.contentResults.length,
              askSuggestionCount: finalResults.askSuggestions.length,
              hasDirectAnswer: finalResults.directAnswer != null,
            });
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error('Failed to stream omnibox query', e);
          if (mountedRef.current && requestIdRef.current === requestId) {
            setErrorMessage('Search failed. Please try again.');
            // Allow retrying the same query after failure
            lastSubmittedQueryRef.current = null;
          }
        });

      // Drop the loading state only once both requests have settled, so the entity results and
      // their attachment section render together instead of the border clearing between them.
      Promise.all([mainSettled, attachmentsSettled])
        .then(() => {
          if (mountedRef.current && requestIdRef.current === requestId) {
            setLoading(false);
          }
        })
        .catch(noop);
    },
    [setResults, setAttachmentResults, setHelpDocsResults, requestIdRef]
  );
  // Keep the ref in sync so defaultActions callbacks always call the latest executeSearch
  executeSearchRef.current = executeSearch;

  // Fire the rendered timing event on the first transition from null → non-null results per request
  const lastLoggedRequestIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (results != null && lastRequestId != null && lastLoggedRequestIdRef.current !== lastRequestId) {
      lastLoggedRequestIdRef.current = lastRequestId;
      logProfilerTimingEvent({...OMNIBOX_SEARCH_RESULTS_RENDERED_EVENT, id: lastRequestId});
    }
  }, [results, lastRequestId]);

  // Automatically re-execute the query when filters change while results are showing.
  // This fires when the user changes a filter or toggles a type pill after having already
  // submitted a search.
  const prevFiltersRef = useRef(searchFilters);
  useEffect(() => {
    const filtersChanged = prevFiltersRef.current !== searchFilters;
    prevFiltersRef.current = searchFilters;

    if (filtersChanged && lastSubmittedQueryRef.current != null) {
      executeSearch(lastSubmittedQueryRef.current, searchFilters, {isRequery: true});
    }
  }, [searchFilters, executeSearch]);

  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Backspace with an empty query removes the last filter
      if (event.key === 'Backspace' && !query && selectedFilters.length > 0) {
        trackEvent(OMNIBOX_ANALYTICS_ACTIONS.REMOVED_FILTER, {
          searchSessionId: searchSessionIdRef.current,
          filterKind: selectedFilters[selectedFilters.length - 1].kind,
        });
        setSelectedFilters((prev) => prev.slice(0, -1));
        return;
      }

      if (event.key === 'Escape') {
        event.stopPropagation();
        handleClose('escape');
        return;
      }

      // Deduplicate consecutive identical submissions; ref is cleared on failure and reopen to allow retry
      if (event.key === 'Enter' && query && query !== lastSubmittedQueryRef.current) {
        event.preventDefault();
        event.stopPropagation();

        executeSearch(query, searchFilters, {isRequery: false});
      }
    },
    [query, selectedFilters, searchFilters, handleClose, executeSearch]
  );

  const {
    hoveredIndex,
    handleKeyDown: handleNavKeyDown,
    handleMouseEnter,
    handleMouseMove,
    scrollContainerRef,
  } = useOmniboxKeyboardNav({
    actions,
    enabled: actions.length > 0,
    onClose: () => {
      handleClose('clicked_result');
    },
    onSelect: (action, index) => {
      const omniAction = action as OmniboxActionItem;
      // The advanced-search row emits CLICKED_ADVANCED_SEARCH via its own handler; skip the generic
      // result-click event so pressing Enter on it isn't double-counted (mirrors the mouse path).
      if (omniAction.resultType !== 'advanced_search') {
        trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_RESULT, {
          searchSessionId: searchSessionIdRef.current,
          resultType: omniAction.resultType,
          index,
          objectType: omniAction.objectType,
          group: omniAction.group,
          filterCount: selectedFilters.length,
          selectionMethod: 'keyboard',
        });
      }
      if (omniAction.resultType === 'search_result' && omniAction.apiId && lastSubmittedQueryRef.current) {
        recordSearch(lastSubmittedQueryRef.current, omniAction.apiId, omniAction.objectType).catch(noop);
      }
    },
    usedArrowKeys: usedArrowKeysRef,
  });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const isInputFocused = event.target === inputRef.current;

      // Escape should work from any focused element
      if (event.key === 'Escape') {
        handleKeyDownCapture(event);
        return;
      }

      // All other custom keyboard handling only applies when the input is focused.
      // Let buttons and other interactive elements handle their own keys natively.
      if (!isInputFocused) {
        return;
      }

      handleKeyDownCapture(event);
      if (!event.defaultPrevented) {
        handleNavKeyDown(event);
      }
    },
    [handleKeyDownCapture, handleNavKeyDown]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      setErrorMessage(null);
      // When the input is cleared, reset to the home screen: drop any active quick filters and
      // reset search state immediately so an in-flight request can't overwrite the cleared UI
      // with stale results.
      if (!value) {
        requestIdRef.current = null;
        lastSubmittedQueryRef.current = null;
        setResults(null);
        setAttachmentResults(null);
        setLoading(false);
        setSelectedFilters([]);
        setActiveTypeToggle(null);
      } else if (results && !loading) {
        setResults(null);
        setAttachmentResults(null);
        lastSubmittedQueryRef.current = null;
      }
    },
    [results, loading, setResults, setAttachmentResults, requestIdRef]
  );

  // Entry / Entity are mutually exclusive type toggles; turning one on replaces a Type dropdown
  // selection.
  const handleTypeToggleClick = useCallback((id: TypeToggleId) => {
    setSelectedFilters((prev) => {
      if (!prev.some((f) => f.kind === 'type' || f.kind === 'schema')) {
        return prev;
      }
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.REMOVED_FILTER, {
        searchSessionId: searchSessionIdRef.current,
        filterKind: 'type',
      });
      return prev.filter((f) => f.kind !== 'type' && f.kind !== 'schema');
    });
    setActiveTypeToggle((prev) => {
      if (prev === id) {
        trackEvent(OMNIBOX_ANALYTICS_ACTIONS.REMOVED_FILTER, {
          searchSessionId: searchSessionIdRef.current,
          filterKind: 'type',
        });
        return null;
      }
      // Switching from another active toggle replaces it, so report the removal of the previous type.
      if (prev != null) {
        trackEvent(OMNIBOX_ANALYTICS_ACTIONS.REMOVED_FILTER, {
          searchSessionId: searchSessionIdRef.current,
          filterKind: 'type',
        });
      }
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.ADDED_FILTER, {
        searchSessionId: searchSessionIdRef.current,
        filterKind: 'type',
      });
      return id;
    });
    inputRef.current?.focus();
  }, []);

  // "Authored By" is a dropdown of author options; only one author can be applied at a time, so
  // selecting an author replaces any existing author filter and re-selecting it clears the filter.
  const handleToggleAuthor = useCallback((author: OmniboxFilter) => {
    setSelectedFilters((prev) => {
      const exists = prev.some((f) => f.kind === 'user' && f.id === author.id);
      const withoutAuthors = prev.filter((f) => f.kind !== 'user');
      if (exists) {
        trackEvent(OMNIBOX_ANALYTICS_ACTIONS.REMOVED_FILTER, {
          searchSessionId: searchSessionIdRef.current,
          filterKind: 'user',
        });
        return withoutAuthors;
      }
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.ADDED_FILTER, {
        searchSessionId: searchSessionIdRef.current,
        filterKind: 'user',
      });
      return [...withoutAuthors, author];
    });
  }, []);

  // The selected author is surfaced on the "Authored By" pill itself (as "Authored by: Name").
  const selectedAuthors = useMemo(() => selectedFilters.filter((f) => f.kind === 'user'), [selectedFilters]);

  // Single-select: picking a type or schema replaces any existing one (and any Entry / Entity toggle);
  // picking the selected one again clears it.
  const handleSelectType = useCallback((item: OmniboxFilter) => {
    setActiveTypeToggle(null);
    setSelectedFilters((prev) => {
      // The same schema can be listed under several options; picking it under another one moves the
      // selection rather than clearing it.
      const exists = prev.some(
        (f) => f.kind === item.kind && f.id === item.id && f.optionKey === item.optionKey
      );
      const withoutType = prev.filter((f) => f.kind !== 'type' && f.kind !== 'schema');
      if (exists) {
        trackEvent(OMNIBOX_ANALYTICS_ACTIONS.REMOVED_FILTER, {
          searchSessionId: searchSessionIdRef.current,
          filterKind: item.kind,
        });
        return withoutType;
      }
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.ADDED_FILTER, {
        searchSessionId: searchSessionIdRef.current,
        filterKind: item.kind,
      });
      return [...withoutType, item];
    });
  }, []);

  const selectedType = useMemo(
    () => selectedFilters.find((f) => f.kind === 'type' || f.kind === 'schema') ?? null,
    [selectedFilters]
  );

  // Single-select: selecting a project or folder replaces any existing one.
  const handleToggleProjectFolder = useCallback((item: OmniboxFilter) => {
    setSelectedFilters((prev) => {
      const exists = prev.some((f) => f.kind === item.kind && f.id === item.id);
      const withoutProjectFolder = prev.filter((f) => f.kind !== 'project' && f.kind !== 'folder');
      if (exists) {
        trackEvent(OMNIBOX_ANALYTICS_ACTIONS.REMOVED_FILTER, {
          searchSessionId: searchSessionIdRef.current,
          filterKind: item.kind,
        });
        return withoutProjectFolder;
      }
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.ADDED_FILTER, {
        searchSessionId: searchSessionIdRef.current,
        filterKind: item.kind,
      });
      return [...withoutProjectFolder, item];
    });
  }, []);

  // Shown on the pill itself, not as a removable chip, so it's excluded from the chip row below.
  const selectedProjectFolders = useMemo(
    () => selectedFilters.filter((f) => f.kind === 'project' || f.kind === 'folder'),
    [selectedFilters]
  );

  // "Advanced search" opens the full search page, carrying over the current query text plus the
  // active type toggle and author filters so the search page restores the same view. Built as a real
  // URL that handleAdvancedSearch feeds to navigateToURL for SPA navigation.
  const advancedSearchHref = useMemo(() => {
    const defaults = getDefaultSearchSearchParams();

    // The AUTHOR filter is populated with user handles (not IDs); the omnibox user filters carry both.
    const authorHandles = selectedFilters
      .filter((f) => f.kind === 'user')
      .map((f) => f.handle)
      .filter((handle): handle is string => handle != null);

    // The default params include a TYPES filter spanning all listable types; narrow it to the selected
    // types. Advanced Search rejects the AUTHOR filter if any selected type has no authors, so with an
    // author selected, also narrow to types that support it.
    const filters = defaults.filters.map((filter) => {
      if (filter.key !== FilterKey.TYPES || !Array.isArray(filter.value)) {
        return filter;
      }
      let types = selectedSearchTypes ?? filter.value;
      if (authorHandles.length > 0) {
        types = getTypesSupportingFilter(FilterKey.AUTHOR, types);
      }
      return {...filter, value: types};
    });
    // A schema, or a type scoped to its own schemas (e.g. Antibody), narrows the schema filter too.
    const selectedTypeFilter = selectedFilters.find((f) => f.kind === 'type' || f.kind === 'schema');
    const schemaIds =
      selectedTypeFilter?.kind === 'schema'
        ? [selectedTypeFilter.id]
        : (selectedTypeFilter?.schemaApiIds ?? []);
    if (schemaIds.length > 0) {
      filters.push({...keyToEmptyFilter(FilterKey.SCHEMA), value: [...schemaIds]});
    }

    if (authorHandles.length > 0) {
      filters.push({...keyToEmptyFilter(FilterKey.AUTHOR), value: authorHandles});
    }

    const searchParams = {...defaults, filters, query};
    // getNavigationURL asserts filterErrors is 1:1 with filters; these filters have no errors.
    const filterErrors = searchParams.filters.map(() => null);
    return getNavigationURL('/search', searchParams, filterErrors);
  }, [query, selectedSearchTypes, selectedFilters]);

  // Advanced Search URL listing entries the current user authored, most recently modified first.
  // The search index has no last-modifier field, so authorship stands in for "my entries".
  const getRecentEntriesHref = (): string => {
    const defaults = getDefaultSearchSearchParams();
    const filters = defaults.filters.map((filter) =>
      filter.key === FilterKey.TYPES ? {...filter, value: [ModelTypeConstants.ENTRY]} : filter
    );
    const currentUserHandle: string | undefined = EntityStore.currentUser()?.handle;
    if (currentUserHandle != null) {
      filters.push({...keyToEmptyFilter(FilterKey.AUTHOR), value: [currentUserHandle]});
    }
    const searchParams = {
      ...defaults,
      filters,
      sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
    };
    return getNavigationURL(
      '/search',
      searchParams,
      searchParams.filters.map(() => null)
    );
  };

  const handleAdvancedSearch = useCallback(() => {
    trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_ADVANCED_SEARCH, {
      searchSessionId: searchSessionIdRef.current,
    });
    handleClose('clicked_result');
    navigateToURL(advancedSearchHref);
  }, [advancedSearchHref, handleClose]);
  advancedSearchTriggerRef.current = handleAdvancedSearch;

  const showDefaultState = !query && !results && selectedFilters.length === 0 && activeTypeToggle == null;

  const showResultsSkeleton = loading && !results;

  // Group actions by group name for section headers.
  // Uses a Map so that items with the same group name are merged even when non-consecutive
  // (e.g. interleaved project/folder items from the API), avoiding duplicate React keys.
  const groupedActions = useMemo(() => {
    const groupMap = new Map<
      string,
      {group: string; actions: Array<{action: OmniboxActionItem; index: number}>}
    >();
    actions.forEach((action, index) => {
      const group = action.group ?? '';
      let entry = groupMap.get(group);
      if (!entry) {
        entry = {group, actions: []};
        groupMap.set(group, entry);
      }
      entry.actions.push({action, index});
    });
    return [...groupMap.values()];
  }, [actions]);

  const renderGroupContent = (group: (typeof groupedActions)[number]): JSX.Element => (
    <div role="group" aria-label={group.group || undefined}>
      {/* Search results sit directly under the input with no visual header (per Figma); the group
          name is still used as the accessible label above and for analytics. */}
      {group.group === 'Recent Searches' ? (
        <div className={classes.recentSearchesHeader}>
          <span className={classes.recentSearchesTitle} aria-hidden="true">
            {group.group}
          </span>
          <Anchor
            href={advancedSearchHref}
            className={classes.advancedSearchLink}
            onClick={(e) => {
              // Let modifier/middle clicks fall through so "open in new tab" works; intercept plain
              // left clicks for in-app navigation with tracking + close.
              if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                return;
              }
              e.preventDefault();
              handleAdvancedSearch();
            }}
          >
            Advanced search
            <BenchlingIcon icon="arrow-right" style={{fontSize: 11}} />
          </Anchor>
        </div>
      ) : (
        group.group &&
        group.group !== SEARCH_RESULTS_GROUP &&
        // The advanced-search escalation is a single self-descriptive row, so it renders headerless
        // like the search results.
        group.group !== ADVANCED_SEARCH_GROUP &&
        // Saved-search rows are self-evidently saved searches (their subtitle reads "Saved search ·
        // …"), so the section header would be redundant; suppress it while keeping the group value
        // for analytics and the accessible label above.
        group.group !== SAVED_SEARCHES_GROUP && (
          <div className={classes.sectionHeader} aria-hidden="true">
            {group.group === ASK_BENCHLING_GROUP && (
              <BenchlingIcon icon="sparkle" style={{fontSize: 12, marginRight: 4}} />
            )}
            {group.group}
          </div>
        )
      )}
      {group.actions.map(({action, index}) => {
        const isNonInteractive = action.resultType === 'help_docs_loading';
        const aiChatContextId = aiChatEnabled ? getAiChatContextId(action) : undefined;
        return (
          <div
            key={action.id}
            id={`omnibox-option-${index}`}
            role="option"
            aria-selected={hoveredIndex === index}
            aria-disabled={isNonInteractive || undefined}
            tabIndex={-1}
            data-action-index={index}
            onMouseEnter={() => {
              handleMouseEnter(index);
            }}
          >
            <OmniboxAction
              action={action}
              hovered={hoveredIndex === index}
              query={query}
              onStartAiChat={
                aiChatContextId
                  ? () => {
                      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_RESULT_AI_CHAT, {
                        searchSessionId: searchSessionIdRef.current,
                        resultType: action.resultType,
                        index,
                        objectType: action.objectType,
                        group: action.group,
                      });
                      // Capture before handleClose clears the ref.
                      const originSearchQuery = lastSubmittedQueryRef.current ?? undefined;
                      handleClose('clicked_result');
                      startAiChatWithResultContext(aiChatContextId, originSearchQuery);
                    }
                  : undefined
              }
              onTrigger={() => {
                // The advanced-search row emits CLICKED_ADVANCED_SEARCH via its own handler; skip the
                // generic result-click event so it isn't double-counted.
                if (action.resultType !== 'advanced_search') {
                  trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_RESULT, {
                    searchSessionId: searchSessionIdRef.current,
                    resultType: action.resultType,
                    index,
                    objectType: action.objectType,
                    group: action.group,
                    filterCount: selectedFilters.length,
                    selectionMethod: 'mouse',
                  });
                }
                if (action.resultType === 'search_result' && action.apiId && lastSubmittedQueryRef.current) {
                  recordSearch(lastSubmittedQueryRef.current, action.apiId, action.objectType).catch(noop);
                }
                action.onTrigger();
                if (action.closeOnTrigger !== false) {
                  handleClose('clicked_result');
                }
              }}
            />
          </div>
        );
      })}
    </div>
  );

  // Live region text for screen reader announcements
  const statusMessage = useMemo(() => {
    if (errorMessage) {
      return errorMessage;
    }
    if (loading) {
      return 'Searching...';
    }
    if (results) {
      const count = actions.length;
      if (count === 0) {
        return 'No results found';
      }
      return `${count} result${count !== 1 ? 's' : ''} found`;
    }
    return '';
  }, [loading, errorMessage, actions.length, results]);

  const placeholder =
    searchFilters.length > 0
      ? 'Search within selected filters...'
      : 'Search, find, and navigate throughout Benchling';

  const composeEnabled = isAgentTypeEnabled(
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? AgentType.ASSISTANT : AgentType.DATA_CAPTURE
  );

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        withCloseButton={false}
        aria-label="Command palette"
        // Width is set explicitly on `content` below (fluid vw). `size` only sets max-width, which the
        // `!important` width override always wins over, so `size="auto"` avoids implying a fixed 660px cap.
        size="auto"
        radius="md"
        centered={false}
        closeOnClickOutside={true}
        overlayProps={{opacity: 0.3}}
        trapFocus={true}
        // Don't restore focus to whatever opened the omnibox on close. Returning focus to the navbar
        // search icon leaves it rendered in its focused (hover-like) state after the omnibox is
        // dismissed; letting focus reset avoids the lingering highlight.
        returnFocus={false}
        styles={() => ({
          root: {zIndex: 1055},
          overlay: {zIndex: 1054},
          // Nucleus MODAL_DEFAULTS pins the inner container to `left: 0`; add `right: 0` + center-justify so
          // the omnibox is horizontally centered rather than pinned to the left edge.
          inner: {
            zIndex: 1055,
            left: 0,
            right: 0,
            justifyContent: 'center',
            paddingTop: 'clamp(20px, 8vh, 120px)',
            paddingBottom: 'clamp(20px, 8vh, 120px)',
          },
          // !important needed to override nucleus-ui MODAL_DEFAULTS which sets body padding/content flex with !important
          content: {
            overflow: 'hidden !important' as never,
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            // Fluid width: 660px in the 1280px design frame -> 51.5625vw, so the omnibox scales with the
            // viewport (larger on wide screens, smaller on narrow ones).
            width: '51.5625vw !important' as never,
            // 12px radius clips the children to the rounded popup shape (`overflow: hidden` above). The
            // GRAY_400 frame is drawn per-section (search header, filter tabs, results, footer) so the
            // side borders line up continuously; see each section's `border*` below.
            borderRadius: 12,
          },
          header: {display: 'none'},
          body: {
            padding: '0 !important' as never,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        })}
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keyboard events delegated from child input */}
        <div onKeyDown={handleKeyDown} data-omnibox-content="" className={classes.contentWrapper}>
          <div aria-live="polite" aria-atomic="true" className={classes.srOnly}>
            {statusMessage}
          </div>
          <QuickSearchInput
            query={query}
            onQueryChange={handleInputChange}
            placeholder={placeholder}
            inputRef={inputRef}
            listboxId="omnibox-listbox"
            expanded={actions.length > 0}
            activeDescendantId={
              hoveredIndex >= 0 && actions.length > 0 ? `omnibox-option-${hoveredIndex}` : undefined
            }
          />

          {showFilterPills && (
            <OmniboxFilterTabs
              activeTabIds={activeTabIds}
              onTabClick={handleTypeToggleClick}
              typeTreeNodes={typeTreeNodes}
              selectedType={selectedType}
              onSelectType={handleSelectType}
              showAuthoredBy={authorFilterSupported}
              selectedAuthors={selectedAuthors}
              onToggleAuthor={handleToggleAuthor}
              selectedProjectFolders={selectedProjectFolders}
              onToggleProjectFolder={handleToggleProjectFolder}
            />
          )}

          {/* Scrollable content area — grows with content, scrolls only when viewport is full */}
          <div ref={scrollContainerRef} onMouseMove={handleMouseMove} className={classes.scrollArea}>
            {showDefaultState && (
              <OmniboxQuickActions
                composeEnabled={composeEnabled}
                onEntryFromCompose={() => {
                  trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_QUICK_ACTION, {
                    searchSessionId: searchSessionIdRef.current,
                    quickAction: 'entry_from_compose',
                  });
                  handleClose('clicked_result');
                  openDraftChatInSidepanel(
                    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? AgentType.ASSISTANT : AgentType.DATA_CAPTURE,
                    '',
                    undefined,
                    undefined,
                    {expanded: false}
                  );
                }}
                onCreateEntry={() => {
                  trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_QUICK_ACTION, {
                    searchSessionId: searchSessionIdRef.current,
                    quickAction: 'create_entry',
                  });
                  handleClose('clicked_result');
                  showCreateEntryModal({});
                }}
                onOpenReviews={() => {
                  trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_QUICK_ACTION, {
                    searchSessionId: searchSessionIdRef.current,
                    quickAction: 'open_reviews',
                  });
                  handleClose('clicked_result');
                  setTaskSearchFilter(null, null);
                }}
                onViewRecentEntries={() => {
                  trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_QUICK_ACTION, {
                    searchSessionId: searchSessionIdRef.current,
                    quickAction: 'recent_entries',
                  });
                  handleClose('clicked_result');
                  navigateToURL(getRecentEntriesHref());
                }}
              />
            )}

            {/* Error message */}
            {errorMessage && (
              <div className={classes.errorMessage} role="alert">
                {errorMessage}
              </div>
            )}

            {showResultsSkeleton && <OmniboxResultsSkeleton />}

            {/* Action groups */}
            <div role="listbox" id="omnibox-listbox" aria-label="Results">
              {groupedActions.map((group) =>
                // The direct answer streams in after the fast search results, so ease it open
                // instead of snapping the list down. Everything else renders immediately, unanimated.
                group.group === ANSWER_GROUP ? (
                  <AnimatedReveal key={group.group}>{renderGroupContent(group)}</AnimatedReveal>
                ) : (
                  <React.Fragment key={group.group}>{renderGroupContent(group)}</React.Fragment>
                )
              )}
            </div>
          </div>

          <OmniboxFooter hasNavigableResults={hasNavigableResults} />
        </div>
      </Modal>
      {opened && (loading || showCompletionFlash) && (
        <AnimatedBorderOverlay loading={loading} showCompletionFlash={showCompletionFlash} />
      )}
    </>
  );
}
